// Finance › Add to wallet (wave 3, W2_Portal_Settings look): credit a
// customer's wallet or a driver's top-up wallet after a bank deposit. One
// card: the person, the amount with the VAT split under it, the deposit
// reference, one button. Errors show under the field that is wrong.
//
// Money, exactly as before: who may use it (admin, finance, call-centre
// supervisor, and an Oromia Bank fleet admin), the amount rules (more than 0,
// at most 1000), the 15% VAT taken off the amount, and the one call
// addToWallet(uid, amountAfterVAT, operatorUid, remark) with the same values
// and types (the amount after VAT is the same two-decimal text). New: a
// question that names the person and the amount before the money moves, and
// "Added" only once the call has finished.
import React, { useMemo, useState } from 'react';
import { useSelector, useDispatch, useStore } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { tokens as c } from '../theme';
import { showToast, confirmDialog, InlineMessage, EmptyState } from '../components/feedback';
import { SectionCard, FieldLabel, FieldError, SrOnly, InfoRow, calmCaps, formatMoney, phoneMatches } from '../components/modern';

const AMOUNT_LIMIT = 1000;
const MAX_OPTIONS = 50;

export default function AddMoney() {
  const { t, i18n } = useTranslation();
  const { addToWallet } = api;
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const userdata = useSelector((state) => state.usersdata);
  const dispatch = useDispatch();
  const store = useStore();

  const [userCombo, setUserCombo] = useState(null);
  const [amountText, setAmountText] = useState('');
  const [remark, setRemark] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const allowPrivate = !!settings.AllowCriticalEditsAdmin;

  // Allow admin, finance and call center supervisor users, and fleetadmins only if a source is 'oromiabank'
  const userType = auth.profile?.usertype;
  const userSource = auth.profile?.source;
  const hasAccess = ['admin', 'finance', 'callcentersupervisor'].includes(userType) || (userType === 'fleetadmin' && userSource === 'oromiabank');

  // The same list as before (the users already loaded), phone and email
  // hidden in demo mode.
  const users = useMemo(() => {
    if (!userdata.users) return null;
    return userdata.users.map((user) => ({
      firstName: user.firstName,
      lastName: user.lastName,
      mobile: user.mobile,
      email: user.email,
      uid: user.id,
      desc:
        user.firstName +
        ' ' +
        user.lastName +
        ' (' +
        (allowPrivate ? user.mobile : t('hidden_demo')) +
        ') ' +
        (allowPrivate ? user.email : t('hidden_demo')),
      pushToken: user.pushToken ? user.pushToken : '',
    }));
  }, [userdata.users, allowPrivate, t]);

  // Calculate amount after 15% VAT deduction (the same arithmetic as before)
  const amountNumber = amountText.trim() === '' ? 0 : Number(amountText.replace(/,/g, ''));
  const amount = Number.isFinite(amountNumber) ? amountNumber : 0;
  const vatSubtraction = (amount - amount / 1.15).toFixed(2);
  const amountAfterVAT = (amount - vatSubtraction).toFixed(2);

  const nameOf = (u) => `${(u && u.firstName) || ''} ${(u && u.lastName) || ''}`.trim();
  const money = (v) => formatMoney(v, settings, true);

  // Match the name as shown, and the phone by its digits when it may be shown.
  const filterOptions = (options, { inputValue }) => {
    const q = String(inputValue || '').trim();
    const ql = q.toLowerCase();
    const out = [];
    for (let i = 0; i < options.length && out.length < MAX_OPTIONS; i += 1) {
      const o = options[i];
      if (!q || String(o.desc || '').toLowerCase().includes(ql) || (allowPrivate && phoneMatches(o.mobile, q))) out.push(o);
    }
    return out;
  };

  const resetForm = () => {
    setUserCombo(null);
    setAmountText('');
    setRemark('');
    setErrors({});
    setFormError(null);
  };

  const handleAddBalance = async () => {
    setFormError(null);
    const next = {};
    if (!(userCombo && userCombo.uid)) next.user = L('form_required', 'This field is required');
    if (!Number.isFinite(amountNumber) || !(amount > 0) || amount > AMOUNT_LIMIT) next.amount = t('valid_amount');
    if (!remark) next.remark = L('form_required', 'This field is required');
    setErrors(next);
    if (Object.keys(next).length) return;

    // Get current user's UID from auth
    const operatorUid = auth.profile?.uid;
    if (!operatorUid) {
      setFormError(t('no_details_error'));
      return;
    }

    const name = nameOf(userCombo) || userCombo.desc;
    const ok = await confirmDialog({
      title: L('add_money_confirm_title', 'Add {{amount}} to {{name}}?', { amount: money(amountAfterVAT), name }),
      body: (
        <Box component="dl" sx={{ m: 0 }}>
          <InfoRow label={t('amount')}>{money(amount)}</InfoRow>
          <InfoRow label={`${t('vat')} (15%)`}>{money(vatSubtraction)}</InfoRow>
          <InfoRow label={t('total')}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {money(amountAfterVAT)}
            </Box>
          </InfoRow>
          <InfoRow label={t('reference_number')}>{remark}</InfoRow>
        </Box>
      ),
      confirmLabel: calmCaps(t('add_to_top_up_title')).trim(),
      cancelLabel: L('confirm_cancel', 'Cancel'),
    });
    if (!ok) return;

    setSaving(true);
    try {
      const pending = dispatch(addToWallet(userCombo.uid, amountAfterVAT, operatorUid, `${remark}`));
      // addToWallet reports a missing account in the payment slice instead
      // of failing its promise. Its first step (UPDATE_WALLET_BALANCE) has
      // already run here, so any newer slice with status 'failed' came from
      // THIS call — also when an earlier attempt failed with the same message.
      const started = store.getState().paymentmethods;
      await pending;
      const after = store.getState().paymentmethods || {};
      if (after !== started && after.status === 'failed') {
        setFormError(after.message || t('no_details_error'));
        return;
      }
      showToast.success(L('add_money_done', 'Added {{amount}} to {{name}}', { amount: money(amountAfterVAT), name }));
      // Reset form after successful submission
      resetForm();
    } catch (err) {
      setFormError((err && err.message) || L('form_save_failed', 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const describedBy = (id, hint) => [hint ? `${id}-hint` : null, errors[id.replace('add-money-', '')] ? `${id}-error` : null].filter(Boolean).join(' ') || undefined;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <SectionCard icon={<AccountBalanceWalletOutlinedIcon />} hue="success" title={calmCaps(t('add_to_wallet_title'))} id="add-money">
        {!hasAccess ? (
          <EmptyState compact icon={<LockOutlinedIcon />} title={t('unauthorized_access')} />
        ) : (
          <Box
            component="form"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              handleAddBalance();
            }}
            sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, columnGap: 2, rowGap: 2.25, mt: 2 }}
          >
            {/* The person (name, phone and email in one line) is two columns wide
                from lg, with the amount and the reference beside it. */}
            <Box sx={{ gridColumn: { xs: '1 / -1', lg: 'span 2' }, minWidth: 0 }}>
              <FieldLabel htmlFor="add-money-user">{t('select_user')}</FieldLabel>
              <Autocomplete
                id="add-money-user"
                value={userCombo}
                options={users || []}
                loading={!users}
                disabled={!users || saving}
                onChange={(event, newValue) => {
                  setUserCombo(newValue);
                  if (errors.user) setErrors((prev) => ({ ...prev, user: null }));
                }}
                autoHighlight
                filterOptions={filterOptions}
                getOptionLabel={(option) => option.desc}
                isOptionEqualToValue={(option, value) => option.desc === value.desc}
                renderOption={(props, option) => (
                  <li {...props} key={option.uid}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ fontWeight: 600, color: c.INK }}>{nameOf(option) || option.desc}</Box>
                      <Box sx={{ fontSize: 13, color: c.INK_2, overflowWrap: 'anywhere' }}>
                        {allowPrivate ? [option.mobile, option.email].filter(Boolean).join(' · ') : t('hidden_demo')}
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!!errors.user}
                    inputProps={{ ...params.inputProps, autoComplete: 'off', 'aria-describedby': describedBy('add-money-user') }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {!users ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              <FieldError id="add-money-user-error">{errors.user}</FieldError>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="add-money-amount">{t('amount')}</FieldLabel>
              <TextField
                id="add-money-amount"
                fullWidth
                value={amountText}
                disabled={saving}
                error={!!errors.amount}
                onChange={(event) => {
                  setAmountText(event.target.value);
                  if (errors.amount) setErrors((prev) => ({ ...prev, amount: null }));
                }}
                inputProps={{ inputMode: 'decimal', autoComplete: 'off', 'aria-describedby': describedBy('add-money-amount', true) }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box component="span" sx={{ color: c.INK_2, fontWeight: 600 }}>
                        {settings.symbol || 'ETB'}
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& input': { fontVariantNumeric: 'tabular-nums' } }}
              />
              <SrOnly id="add-money-amount-hint">
                {t('add_money_amount_limit', { amount: AMOUNT_LIMIT, defaultValue: 'Amount should not exceed {{amount}} ETB *' }).replace(/\s*\*\s*$/, '')}
              </SrOnly>
              <FieldError id="add-money-amount-error">{errors.amount}</FieldError>
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <FieldLabel htmlFor="add-money-remark">{t('reference_number')}</FieldLabel>
              <TextField
                id="add-money-remark"
                fullWidth
                value={remark}
                disabled={saving}
                error={!!errors.remark}
                onChange={(e) => {
                  setRemark(e.target.value);
                  if (errors.remark) setErrors((prev) => ({ ...prev, remark: null }));
                }}
                inputProps={{ autoComplete: 'off', 'aria-describedby': describedBy('add-money-remark') }}
              />
              <FieldError id="add-money-remark-error">{errors.remark}</FieldError>
            </Box>

            {/* What the person gets: the amount less 15% VAT */}
            <Box
              component="dl"
              aria-live="polite"
              sx={{ gridColumn: '1 / -1', m: 0, px: 2, py: 0.5, borderRadius: '12px', bgcolor: c.SURFACE_ALT, fontVariantNumeric: 'tabular-nums' }}
            >
              <InfoRow label={t('amount')}>{money(amount)}</InfoRow>
              <InfoRow label={`${t('vat')} (15%)`}>{money(vatSubtraction)}</InfoRow>
              <InfoRow label={t('total')}>
                <Box component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
                  {money(amountAfterVAT)}
                </Box>
              </InfoRow>
            </Box>

            {formError ? (
              <InlineMessage kind="error" sx={{ gridColumn: '1 / -1' }}>
                {formError}
              </InlineMessage>
            ) : null}

            <Box sx={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AccountBalanceWalletOutlinedIcon />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {saving ? L('form_saving', 'Saving…') : calmCaps(t('add_to_top_up_title')).trim()}
              </Button>
            </Box>
          </Box>
        )}
      </SectionCard>
    </Box>
  );
}
